// src/components/Layout.jsx
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  Box,
  Stack,
  CircularProgress,
} from '@mui/material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useContext, useEffect, useMemo, useState } from 'react';
import { UserContext } from '../contexts/UserContext';
import Chat from './Chat';

// ⬇️ Use your custom images for the resources
import goldPileImg from '../data/goldPile.jpg';
import soulDustImg from '../data/soulDust.jpg';

const GOLD = '#d4af37';
const GOLD_SOFT = 'rgba(212,175,55,0.75)';
const GOLD_DIM = 'rgba(212,175,55,0.6)';
const TEXT_WARM = '#f9f6e3';
const BLACK = '#000';
const BLACK_DEEP = '#0a0a0a';

function NavButton({ to, label, isActive }) {
  return (
    <Button
      component={Link}
      to={to}
      size="small"
      sx={{
        px: 1.5,
        color: isActive ? TEXT_WARM : GOLD_DIM,
        textTransform: 'none',
        letterSpacing: 0.4,
        fontWeight: 600,
        '&:hover': { color: TEXT_WARM, textShadow: `0 0 8px ${GOLD_SOFT}` },
        ...(isActive && {
          borderBottom: `2px solid ${GOLD}`,
          borderRadius: 0,
          textShadow: `0 0 10px ${GOLD_SOFT}`,
        }),
      }}
    >
      {label}
    </Button>
  );
}

function Layout({ children }) {
  const { userData, loading } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();

  const isLoginRoute = location.pathname === '/login';

  /**
   * ✅ ONBOARDING GATE
   * If userData exists but is missing required onboarding fields,
   * DO NOT redirect off /login (let the Login dialog run).
   */
  const needsOnboarding = useMemo(() => {
    if (!userData) return false;

    const hasUsername =
      typeof userData.username === 'string' && userData.username.trim().length > 0;
    const hasFaction =
      typeof userData.faction === 'string' && userData.faction.trim().length > 0;
    const hasCards = Array.isArray(userData.ownedCards) && userData.ownedCards.length > 0;

    return !(hasUsername && hasFaction && hasCards);
  }, [userData]);

  // Debounced display values to avoid flicker from optimistic updates vs snapshots
  const [displayCoins, setDisplayCoins] = useState(0);
  const [displaySoulDust, setDisplaySoulDust] = useState(0);
  const [displayTickets, setDisplayTickets] = useState(0);

  useEffect(() => {
    if (loading) return;

    // Not logged in → always go to login (unless already there)
    if (!userData && !isLoginRoute) {
      navigate('/login', { replace: true });
      return;
    }

    // Logged in BUT onboarding incomplete → force to login for setup
    if (userData && needsOnboarding && !isLoginRoute) {
      navigate('/login', { replace: true });
      return;
    }

    // Logged in AND onboarding complete → leaving /login is OK
    if (userData && !needsOnboarding && isLoginRoute) {
      navigate('/', { replace: true });
      return;
    }

    // If userData exists and needsOnboarding and we ARE on /login:
    // ✅ stay here so Login can collect username/faction and grant starter card.
  }, [userData, loading, isLoginRoute, needsOnboarding, navigate]);

  const isActive = useMemo(
    () => (path) => location.pathname === path,
    [location.pathname]
  );

  // When userData changes, gently update the display values with a tiny debounce.
  useEffect(() => {
    if (!userData) return;

    const rawCoins = userData?.coins ?? 0;
    const rawSoulDust = userData?.consumables?.soulDust ?? 0;
    const rawDrawTickets = userData?.drawTickets ?? {};
    const rawTicketsTotal = Object.values(rawDrawTickets).reduce(
      (sum, value) => sum + (value ?? 0),
      0
    );

    const id = setTimeout(() => {
      setDisplayCoins(rawCoins);
      setDisplaySoulDust(rawSoulDust);
      setDisplayTickets(rawTicketsTotal);
    }, 120);

    return () => clearTimeout(id);
  }, [userData]);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100dvh',
          display: 'grid',
          placeItems: 'center',
          background: `radial-gradient(circle at center, ${BLACK} 0%, ${BLACK_DEEP} 100%)`,
          color: TEXT_WARM,
        }}
      >
        <Stack alignItems="center" spacing={2}>
          <CircularProgress
            size={28}
            sx={{
              color: GOLD,
              filter: 'drop-shadow(0 0 6px rgba(212,175,55,0.6))',
            }}
          />
        </Stack>
      </Box>
    );
  }

  /**
   * ✅ IMPORTANT:
   * Allow /login to render even if userData exists,
   * as long as onboarding is incomplete.
   */
  if (isLoginRoute && (!userData || needsOnboarding)) {
    return (
      <Container
        maxWidth="sm"
        sx={{
          color: TEXT_WARM,
          mt: 8,
          pb: 6,
          background: 'transparent',
        }}
      >
        {children}
      </Container>
    );
  }

  // If not on /login and no userData, render nothing (navigation effect above will redirect)
  if (!userData) return null;

  // Safe accessors for header stats
  const league = userData?.league ?? '—';

  // Reusable pill styles
  const pillSx = {
    mr: 1.5,
    px: 1,
    py: 0.25,
    border: `1px solid rgba(212,175,55,0.35)`,
    borderRadius: 1.5,
    background: 'rgba(12,12,12,0.8)',
    boxShadow: `inset 0 0 10px rgba(212,175,55,0.08)`,
  };
  const pillTextSx = {
    color: TEXT_WARM,
    fontWeight: 700,
    letterSpacing: 0.5,
  };

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          background: `linear-gradient(180deg, rgba(212,175,55,0.08) 0%, rgba(0,0,0,0) 50%), ${BLACK_DEEP}`,
          borderBottom: `1px solid rgba(212,175,55,0.25)`,
          boxShadow: `0 2px 24px rgba(212,175,55,0.12)`,
          backdropFilter: 'blur(6px)',
        }}
      >
        <Toolbar sx={{ gap: 1 }}>
          <Typography
            variant="h4"
            sx={{
              flexGrow: 1,
              fontFamily: `'Cinzel', serif`,
              color: GOLD,
              letterSpacing: 1.2,
              textShadow: `0 0 14px rgba(212,175,55,0.35)`,
              userSelect: 'none',
            }}
          >
            Beasts of Legend
          </Typography>

          {/* Resource / status display */}
          <Stack direction="row" alignItems="center" spacing={1}>
            {/* Coins */}
            <Stack direction="row" alignItems="center" spacing={0.75} sx={pillSx}>
              <Box
                component="img"
                src={goldPileImg}
                alt="Coins"
                sx={{
                  width: 18,
                  height: 18,
                  borderRadius: 0.75,
                  objectFit: 'cover',
                  boxShadow: `0 0 8px ${GOLD_SOFT}`,
                  border: `1px solid ${GOLD_DIM}`,
                }}
              />
              <Typography variant="body2" sx={pillTextSx}>
                {displayCoins} coins
              </Typography>
            </Stack>

            {/* Soul Dust */}
            <Stack direction="row" alignItems="center" spacing={0.75} sx={pillSx}>
              <Box
                component="img"
                src={soulDustImg}
                alt="Soul Dust"
                sx={{
                  width: 18,
                  height: 18,
                  borderRadius: 0.75,
                  objectFit: 'cover',
                  boxShadow: `0 0 8px ${GOLD_SOFT}`,
                  border: `1px solid ${GOLD_DIM}`,
                }}
              />
              <Typography variant="body2" sx={pillTextSx}>
                {displaySoulDust} Soul Dust
              </Typography>
            </Stack>

            {/* Free Draw Tickets - only show if user has any */}
            {displayTickets > 0 && (
              <Stack direction="row" alignItems="center" spacing={0.75} sx={pillSx}>
                <Typography variant="body2" sx={pillTextSx}>
                  {displayTickets} Free Draw Ticket{displayTickets === 1 ? '' : 's'}
                </Typography>
              </Stack>
            )}

            {/* League */}
            <Stack direction="row" alignItems="center" spacing={0.75} sx={pillSx}>
              <Typography variant="body2" sx={{ color: GOLD, fontWeight: 800 }}>
                🏆
              </Typography>
              <Typography variant="body2" sx={pillTextSx}>
                {league} League
              </Typography>
            </Stack>
          </Stack>

          {/* Navigation */}
          <Stack direction="row" spacing={0.5}>
            <NavButton to="/" label="Home" isActive={isActive('/')} />
            <NavButton to="/arena" label="Arena" isActive={isActive('/arena')} />
            <NavButton to="/shop" label="Shop" isActive={isActive('/shop')} />
            <NavButton to="/profile" label="Profile" isActive={isActive('/profile')} />
            <NavButton to="/bazzar" label="Bazaar" isActive={isActive('/bazzar')} />
            <NavButton to="/trade" label="Trades" isActive={isActive('/trade')} />
            <NavButton to="/guild" label="Guild" isActive={isActive('/guild')} />
          </Stack>
        </Toolbar>
      </AppBar>

      <Container
        maxWidth="lg"
        sx={{
          color: TEXT_WARM,
          mt: 4,
          pb: 6,
          background: 'transparent',
        }}
      >
        <Box sx={{ mt: 1 }}>{children}</Box>
      </Container>

      <Chat />
    </>
  );
}

export default Layout;
